import { useEffect, useState } from "react";

function PoliciesPage({ onBack, canCreateTickets, apiFetch }) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [ticketMessage, setTicketMessage] = useState("");
  const [sendingTicket, setSendingTicket] = useState(false);
  const [ccTicket, setCcTicket] = useState("");
  const [mostrarVistaTicket, setMostrarVistaTicket] = useState(false);

  const obtenerOwnership = (row) => {
    const ownership = String(row.managedDeviceOwnerType || row.ownerType || "").toLowerCase();
    if (ownership === "company") return "Company";
    if (ownership === "personal") return "Personal";
    return ownership || "Unknown";
  };

  useEffect(() => {
    apiFetch("/api/policies")
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "No fue posible cargar las políticas.");
        return data;
      })
      .then((data) => setPolicies(data.value || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [apiFetch]);

  const resumenDescripcion = (description = "") => {
    const texto = description.replace(/\s+/g, " ").trim();
    return texto.length > 120 ? `${texto.slice(0, 117)}...` : texto || "Sin descripción";
  };

  const cargarDetalle = async (policy, status) => {
    setDetailLoading(true);
    try {
      const response = await apiFetch(`/api/policies/${encodeURIComponent(policy.id)}/statuses`);
      const responseText = await response.text();
      const data = responseText ? JSON.parse(responseText) : {};
      if (!response.ok) throw new Error(data.error || "No fue posible cargar el detalle de equipos.");
      const normalizarEstado = (valor) => String(valor || "").toLowerCase().replace(/[ _-]/g, "");
      setDetail({ policy, status, rows: (data.value || []).filter((row) => normalizarEstado(row.assignmentStatus || row.AssignmentStatus) === normalizarEstado(status)) });
    } catch (err) { setError(err.message); }
    finally { setDetailLoading(false); }
  };

  const crearTicketPolitica = async () => {
    if (!detail || detail.rows.length === 0) return;
    setSendingTicket(true);
    setTicketMessage("");
    const ccDirecciones = ccTicket.split(";").map((correo) => correo.trim()).filter(Boolean);
    const correosInvalidos = ccDirecciones.filter((correo) => !/^([^\s@]+)@([^\s@]+)\.([^\s@]+)$/.test(correo));
    if (correosInvalidos.length > 0) {
      setTicketMessage(`Correo(s) CC inválido(s): ${correosInvalidos.join(", ")}.`);
      setSendingTicket(false);
      return;
    }
    const estadoTicket = detail.status === "not applicable" ? "Not applicable" : detail.status.charAt(0).toUpperCase() + detail.status.slice(1);
    const escaparHtml = (value) => String(value || "-").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
    const escaparCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv = [
      ["Usuario", "PC", "País", "Sistema operativo", "Compliance", "Ownership"],
      ...detail.rows.map((row) => [row.userDisplayName, row.deviceName, row.country, row.operatingSystem, row.complianceState, obtenerOwnership(row)])
    ].map((fila) => fila.map(escaparCsv).join(",")).join("\r\n");
    const contenidoCsv = btoa(unescape(encodeURIComponent("\uFEFF" + csv)));
    const nombreArchivoSeguro = (valor) => String(valor || "politica")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9._-]+/g, "_")
      .replace(/^\.+|\.+$/g, "")
      .slice(0, 80) || "politica";
    try {
      const response = await apiFetch("/api/tickets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: `Inconsistencia Intune: ${detail.policy.name} - ${estadoTicket} (${detail.rows.length} equipos)`,
          htmlBody: `<p>Hemos detectado que la política <strong>${escaparHtml(detail.policy.name)}</strong> tiene <strong>${detail.rows.length}</strong> equipos en estado <strong>${escaparHtml(estadoTicket)}</strong>. El detalle se encuentra en el archivo CSV adjunto.</p>`,
          attachment: { name: `politica_${nombreArchivoSeguro(detail.policy.name)}_${nombreArchivoSeguro(estadoTicket)}.csv`, contentType: "text/csv", contentBytes: contenidoCsv },
          cc: ccDirecciones.join(";")
        })
      });
      const responseText = await response.text();
      const data = responseText ? JSON.parse(responseText) : {};
      if (!response.ok) throw new Error(data.error || "No fue posible enviar el ticket.");
      setMostrarVistaTicket(false);
      setTicketMessage("Ticket enviado correctamente.");
    } catch (err) {
      setTicketMessage(`Error al enviar el ticket: ${err.message}`);
    } finally {
      setSendingTicket(false);
    }
  };

  if (detail && !detailLoading) {
    const permiteTicket = ["pending", "error", "not applicable"].includes(detail.status.toLowerCase());
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#F4F6F9", padding: "30px", fontFamily: "Segoe UI" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "25px" }}>
          <button onClick={() => { setDetail(null); setTicketMessage(""); }} style={{ backgroundColor: "#6C757D", color: "white", border: "none", borderRadius: "8px", padding: "11px 18px", cursor: "pointer" }}>
            ← Políticas
          </button>
          <div>
            <h1 style={{ color: "#0D5CAB", margin: 0 }}>Detalle de equipos</h1>
            <p style={{ color: "#536273", margin: "5px 0 0" }}>{detail.policy.name} · Estado: {detail.status}</p>
          </div>
        </div>

        <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.15)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
            <h2 style={{ margin: 0, color: "#0D5CAB" }}>Equipos ({detail.rows.length})</h2>
            {canCreateTickets && permiteTicket && (
              <button onClick={() => { setCcTicket(""); setTicketMessage(""); setMostrarVistaTicket(true); }} disabled={sendingTicket} style={{ backgroundColor: "#8E44AD", color: "white", border: "none", borderRadius: "8px", padding: "10px 15px", cursor: "pointer" }}>
                Crear ticket
              </button>
            )}
          </div>
          {ticketMessage && <p style={{ color: ticketMessage.startsWith("Error") ? "#B00020" : "#16803C" }}>{ticketMessage}</p>}
          {mostrarVistaTicket && <div style={{ marginBottom: "15px", padding: "15px", border: "1px solid #D9E2EC", borderRadius: "8px", backgroundColor: "#F8FAFC" }}><h3 style={{ marginTop: 0 }}>Vista previa del ticket</h3><p>Se enviará a <strong>soportetecnico@dichter-neira.com</strong> desde <strong>jromero@dichter-neira.com</strong>.</p><p><strong>Política:</strong> {detail.policy.name}</p><p><strong>Estado:</strong> {detail.status} · <strong>Equipos:</strong> {detail.rows.length}</p><label style={{ display: "block", fontWeight: "600" }}>CC</label><input value={ccTicket} onChange={(event) => setCcTicket(event.target.value)} placeholder="correo1@empresa.com; correo2@empresa.com" style={{ width: "100%", boxSizing: "border-box", padding: "10px", marginTop: "6px", border: "1px solid #ccc", borderRadius: "6px" }} /><small style={{ color: "#687684" }}>Separa varios correos con punto y coma (;).</small><div style={{ marginTop: "12px", display: "flex", gap: "8px" }}><button onClick={() => setMostrarVistaTicket(false)} style={{ ...countButton("#6C757D"), textDecoration: "none" }}>Cancelar</button><button onClick={crearTicketPolitica} disabled={sendingTicket} style={{ backgroundColor: "#8E44AD", color: "white", border: "none", borderRadius: "6px", padding: "9px 14px", cursor: "pointer" }}>{sendingTicket ? "Enviando..." : "Confirmar y enviar"}</button></div></div>}
          {detail.rows.length === 0 ? <p>No hay equipos para el estado seleccionado.</p> : (
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
              <thead><tr><th style={th}>Usuario</th><th style={th}>PC</th><th style={th}>País</th><th style={th}>Sistema operativo</th><th style={th}>Compliance</th><th style={th}>Ownership</th></tr></thead>
              <tbody>{detail.rows.map((row, index) => <tr key={`${row.intuneDeviceId || row.deviceName}-${index}`}><td style={td}>{row.userDisplayName}</td><td style={td}>{row.deviceName}</td><td style={td}>{row.country}</td><td style={td}>{row.operatingSystem}</td><td style={td}>{row.complianceState}</td><td style={td}>{obtenerOwnership(row)}</td></tr>)}</tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#F4F6F9", padding: "30px", fontFamily: "Segoe UI" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px" }}>
        <div>
          <h1 style={{ color: "#0D5CAB", margin: 0 }}>Políticas de Intune</h1>
          <p style={{ color: "#536273" }}>Políticas de configuración implementadas</p>
          {(loading || detailLoading) && <p style={{ color: "#0D5CAB", fontWeight: "600" }}>Cargando información...</p>}
        </div>
        <button onClick={onBack} style={{ backgroundColor: "#0D5CAB", color: "white", border: "none", borderRadius: "8px", padding: "11px 18px", cursor: "pointer" }}>
          ← Dashboard
        </button>
      </div>

      {error && <p style={{ color: "#B00020" }}>{error}</p>}
      {!loading && !error && (
        <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.15)" }}>
          <h3 style={{ color: "#0D5CAB", margin: "0 0 20px" }}>
            POLÍTICAS ({policies.length})
          </h3>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
            <thead><tr><th style={th}>Política</th><th style={th}>Descripción</th><th style={th}>Success</th><th style={th}>Pending</th><th style={th}>Error</th><th style={th}>Not applicable</th><th style={th}>Cumplimiento</th></tr></thead>
            <tbody>
              {policies.map((policy) => (
                <tr key={policy.id}>
                  <td style={td}>{policy.name || "-"}</td>
                  <td style={td} title={policy.description || ""}>{resumenDescripcion(policy.description)}</td>
                  <td style={td}><button onClick={() => cargarDetalle(policy, "success")} style={countButton("#16803C")}>{policy.succeeded || 0}</button></td>
                  <td style={td}><button onClick={() => cargarDetalle(policy, "pending")} style={countButton("#A66B00")}>{policy.pending || 0}</button></td>
                  <td style={td}><button onClick={() => cargarDetalle(policy, "error")} style={countButton("#B00020")}>{policy.error || 0}</button></td>
                  <td style={td}><button onClick={() => cargarDetalle(policy, "not applicable")} style={countButton("#6C757D")}>{policy.notApplicable || 0}</button></td>
                  <td style={{ ...td, fontWeight: "700", color: policy.compliancePercentage >= 100 ? "#16803C" : "#0D5CAB" }}>{Number(policy.compliancePercentage || 0).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          {policies.length === 0 && <p>No se encontraron políticas.</p>}
        </div>
      )}
      {detail && !detailLoading && <div style={{ marginTop: "20px", backgroundColor: "white", borderRadius: "12px", padding: "20px" }}><div style={{ display: "flex", alignItems: "center", gap: "12px" }}><h2 style={{ flex: 1 }}>{detail.policy.name} - {detail.status}</h2>{canCreateTickets && (detail.status.toLowerCase() === "not applicable" || detail.status.toLowerCase() === "pending") && <button onClick={crearTicketPolitica} disabled={sendingTicket} style={{ backgroundColor: "#8E44AD", color: "white", border: "none", borderRadius: "8px", padding: "10px 14px", cursor: "pointer" }}>{sendingTicket ? "Enviando..." : "Crear ticket"}</button>}</div>{ticketMessage && <p style={{ color: ticketMessage.startsWith("Error") ? "#B00020" : "#16803C" }}>{ticketMessage}</p>}{detail.rows.length === 0 ? <p>No hay equipos para el estado seleccionado.</p> : <table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr><th style={th}>Usuario</th><th style={th}>PC</th><th style={th}>País</th><th style={th}>Sistema operativo</th><th style={th}>Compliance</th></tr></thead><tbody>{detail.rows.map((row, index) => <tr key={`${row.intuneDeviceId || row.deviceName}-${index}`}><td style={td}>{row.userDisplayName}</td><td style={td}>{row.deviceName}</td><td style={td}>{row.country}</td><td style={td}>{row.operatingSystem}</td><td style={td}>{row.complianceState}</td></tr>)}</tbody></table>}</div>}
    </div>
  );
}

const th = { textAlign: "left", padding: "12px", backgroundColor: "#0D5CAB", color: "white" };
const td = { padding: "12px", borderBottom: "1px solid #E1E6EC" };
const countButton = (color) => ({ border: "none", background: "transparent", color, fontWeight: "700", fontSize: "15px", textDecoration: "underline", cursor: "pointer" });

export default PoliciesPage;
